/**
 * Where an experiment applies — derived from what it actually changes.
 *
 * `targeting.url_patterns` was a free-text field defaulting to empty, and empty means "every page".
 * That is not a targeting decision, it is the absence of one, and it silently inflates the exposure
 * denominator: every session on the site counts as having been in the experiment, including the
 * ones that never loaded the page the variant touches. On Naturalú (aug-2026) a homepage banner
 * test counted ~18,700 weekly sessions instead of the ~5,600 that reached the homepage — a ~3×
 * dilution of the measured rate, invisible in the results.
 *
 * The scope does not need to be typed, because the config already states it. Every modification,
 * hide, insertion and swap the visual editor emits carries its own `urlPattern`; the experiment can
 * only have an effect where at least one of them applies. The union of those patterns IS the
 * experiment's scope.
 */

/** Config shapes the visual editor emits. Each entry carries the pattern it applies on. */
interface ScopedEntry {
  urlPattern?: string
}

export interface VariantConfigLike {
  modifications?: ScopedEntry[]
  elements_to_hide?: ScopedEntry[]
  elements_to_insert?: ScopedEntry[]
  elements_to_swap?: ScopedEntry[]
  plp_propagations?: ScopedEntry[]
}

/** `/*` and `*` both mean "everywhere"; so does an empty pattern, by omission. */
const isGlobal = (p: string) => p === '/*' || p === '*'

/**
 * The comma-separated `url_patterns` string for a config.
 *
 * Comma-separated is the wire contract: `tnx-core.filterExperimentsByUrl` and the bundle template
 * both `.split(',')` this field, and an array throws in both (the aug-2026 incident that stopped
 * every experiment on a store).
 *
 * Returns '' when nothing is scoped — which the caller must treat as "could not derive", not as
 * "site-wide", since those are the same string but very different facts.
 */
export function deriveUrlPatterns(config: VariantConfigLike | null | undefined): string {
  if (!config) return ''

  const patterns = new Set<string>()
  const entryLists = [
    config.modifications,
    config.elements_to_hide,
    config.elements_to_insert,
    config.elements_to_swap,
    config.plp_propagations,
  ]

  for (const list of entryLists) {
    for (const entry of list || []) {
      const pattern = (entry?.urlPattern || '').trim()
      if (!pattern) continue
      // One global entry makes the whole experiment global — no narrower union can hold, because
      // that entry genuinely applies everywhere.
      if (isGlobal(pattern)) return '/*'
      patterns.add(pattern)
    }
  }

  // Deterministic order so re-saving an unchanged config doesn't appear to change the targeting.
  return Array.from(patterns).sort().join(',')
}

/**
 * Human summary of a pattern string, for the line under the picker.
 * Deliberately blunt about the site-wide case, which is the one that costs measurement.
 */
export function describeScope(urlPatterns: string): string {
  const patterns = (urlPatterns || '').split(',').map((p) => p.trim()).filter(Boolean)

  if (patterns.length === 0) {
    return 'Sin definir: el experimento se cuenta en todas las sesiones del sitio, incluso las que nunca ven el cambio.'
  }
  if (patterns.some(isGlobal)) {
    return 'Todo el sitio: cada sesión cuenta como expuesta, aunque no haya visto el cambio.'
  }
  if (patterns.length === 1) {
    return patterns[0].includes('*')
      ? `Solo las páginas que empiezan con ${patterns[0].replace(/\*$/, '')}`
      : `Solo ${patterns[0]}`
  }
  return `Solo estas páginas: ${patterns.join(', ')}`
}
